import React from 'react';
import { motion } from 'framer-motion';
import { GiftIcon } from 'lucide-react';
import { cn } from '../lib/utils';
import { bonuses, type BonusAccent } from '../data/system';
const accentStyles: Record<BonusAccent, {
  badge: string;
  dot: string;
}> = {
  electric: {
    badge: 'bg-electric/10 text-electric-deep',
    dot: 'bg-electric'
  },
  perf: {
    badge: 'bg-perf/15 text-perf-deep',
    dot: 'bg-perf'
  },
  ember: {
    badge: 'bg-ember/20 text-ember-deep',
    dot: 'bg-ember'
  },
  aqua: {
    badge: 'bg-aqua/15 text-aqua-deep',
    dot: 'bg-aqua'
  }
};
export function Bonuses() {
  return <section aria-label="Bonuses" className="bg-white" data-id="element-18" data-mp="3o1lf0qlfzjix">
      <div className="mx-auto max-w-6xl px-5 py-16 lg:py-24" style={{
      paddingTop: "17px",
      paddingBottom: "21px"
    }} data-id="element-19" data-mp="3o3696f3uh0g9">
        <div className="text-center" data-id="element-20" data-mp="3t0y99wen7svj">
          <p className="inline-flex items-center gap-1.5 rounded-full bg-ember/20 px-3 py-1 text-xs font-bold uppercase tracking-wider text-ember-deep" data-id="element-21" data-mp="1mai4s2fgs6yw">
            Today only
          </p>
          <h2 className="mt-4 font-display text-4xl font-extrabold uppercase leading-none text-navy sm:text-6xl" data-id="element-22" data-mp="3ie6h5f5nxxum">
            4 free performance bonuses included
          </h2>
        </div>

        <div className="mt-12 grid gap-5 md:grid-cols-2" data-id="element-23" data-mp="3r6qjapl5ykk7">
          {bonuses.map((b, i) => {
          const a = accentStyles[b.accent];
          return <motion.article key={b.n} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-40px'
          }} transition={{
            duration: 0.3,
            delay: i % 2 * 0.05,
            ease: [0.23, 1, 0.32, 1]
          }} className="flex h-full flex-col rounded-2xl border border-navy/10 bg-warm p-5 sm:p-6" data-id="element-24" data-mp="3n67p4ovst2eu" data-mpi={i}>
                <div className="flex gap-4 sm:gap-5" data-id="element-25" data-mp="33ge3ksjofjj2">
                  <img src={b.cover} alt={`${b.title} cover`} width={1122} height={1402} loading="lazy" decoding="async" className="h-auto w-24 shrink-0 self-start drop-shadow-[0_12px_16px_rgba(16,28,44,0.25)] sm:w-32" data-id="element-26" data-mp="nq1dx6xbhgw4" />
                  <div className="min-w-0 flex-1" data-id="element-27" data-mp="3cjppd29wr0po">
                    <div className="flex flex-wrap items-center justify-between gap-2" data-id="element-28" data-mp="3cl0rkxnpg0eo">
                      <span className={cn('inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-bold', a.badge)} data-id="element-29" data-mp="3hg6enzsufl4c">
                        <GiftIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-30" data-mp="3qj7ituy5f25d" /> Bonus {b.n}
                      </span>
                      <span className="whitespace-nowrap text-sm font-bold" data-id="element-31" data-mp="2ttgcmyghvqjq">
                        <span className="text-navy/40 line-through" data-id="element-32" data-mp="2xtii7ipcp44a">{b.price}</span>{' '}
                        <span className="text-perf-deep" data-id="element-33" data-mp="32td5zddtxdl4">FREE</span>
                      </span>
                    </div>
                    <h3 className="mt-3 font-display text-xl font-extrabold uppercase leading-tight text-navy sm:text-2xl" data-id="element-34" data-mp="37k3akoeflcer">
                      {b.title}
                    </h3>
                    <p className="mt-1.5 text-sm font-semibold leading-snug text-navy/65" data-id="element-35" data-mp="2f391z4wqbbtl">{b.subtitle}</p>
                  </div>
                </div>
                <div className="mt-4 space-y-2 text-sm leading-relaxed text-navy/70" data-id="element-36" data-mp="27j8an3njtdwq">
                  {b.desc.map(d => <p key={d} data-id="element-37" data-mp="22lpx0zyl6aie">{d}</p>)}
                </div>
                <ul className="mt-auto flex flex-wrap gap-1.5 pt-5" data-id="element-38" data-mp="1xl28qm1qs7rv">
                  {b.tags.map(t => <li key={t} className="inline-flex items-center gap-1.5 rounded-md border border-navy/10 bg-white px-2 py-1 text-xs font-medium text-navy/75" data-id="element-39" data-mp="1tkrs8fgbpyyr">
                      <span className={cn('h-1.5 w-1.5 rounded-full', a.dot)} aria-hidden="true" data-id="element-40" data-mp="1on8cfb3z9e4p" />
                      {t}
                    </li>)}
                </ul>
              </motion.article>;
        })}
        </div>

        <div className="mt-10 rounded-2xl bg-navy px-6 py-8 text-center text-white sm:px-10" data-id="element-41" data-mp="19w9cspl4dzx9">
          <p className="font-display text-2xl font-extrabold uppercase leading-tight sm:text-3xl" data-id="element-42" data-mp="15uo0p6getpo4">
            4 performance tools — <span className="text-perf" data-id="element-43" data-mp="10v1ei7ilsz36">included free</span> today
          </p>
          <p className="mt-2 text-sm text-white/65" data-id="element-44" data-mp="30xspt672660w">
            Total value <span className="line-through" data-id="element-45" data-mp="30ym1mlkvjlpw">$120 USD</span> · With THE FULL DISTANCE TRIATHLON SYSTEM™
          </p>
          <p className="mt-6 text-sm text-white/70" data-id="element-46" data-mp="351ihi4j153q5">
            You don't just receive the main system. You receive the tools designed to help you:
          </p>
          <p className="mt-3 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 font-display text-2xl font-bold uppercase tracking-wide" data-id="element-47" data-mp="2w5sx9ytbifec">
            <span data-id="element-48" data-mp="2m8wmtnjuzj75">Plan</span>
            <span className="text-electric" aria-hidden="true" data-id="element-49" data-mp="2m96edfkjxyey">→</span>
            <span data-id="element-50" data-mp="2895273yu74k6">Fuel</span>
            <span className="text-electric" aria-hidden="true" data-id="element-51" data-mp="289wyyi4678c1">→</span>
            <span data-id="element-52" data-mp="2d889kv2ahmvb">Prepare</span>
            <span className="text-electric" aria-hidden="true" data-id="element-53" data-mp="2d7gdzk8ozp51">→</span>
            <span data-id="element-54" data-mp="6hs7t8uhwf2d">Track</span>
          </p>
        </div>
      </div>
    </section>;
}